import React from 'react';
import { useSpring, animated } from 'react-spring';
import styled, { css } from 'styled-components';
import { size } from '../../../style/util';
import { Times } from '../../common/Icons/Light';
import { ILokalplan } from '../../../Interfaces/ILokalplan';
import { common } from '../../../style';
import { neutral } from '../../../style/colors';
import { useMedia } from '../../../hooks/useMedia';
import { PositionProperty } from 'csstype';
import LokalplanDetails from './LokalplanDetails';

const LokalplanSidebar = (props: ILokalplanSidebarPropType) => {
  const {
    currentLokalplan,
    onClose,
    hoveredPlans,
    clickedLokalplans,
    setCurrentLokaplan,
    canShow,
  } = props;
  const isSmallScreen = useMedia(
    [`(max-width: ${size.small}px)`],
    [true],
    false
  );
  const navAnimation = useSpring({
    config: { duration: 300 },
    right: !isSmallScreen
      ? canShow && currentLokalplan
        ? '0px'
        : '-430px'
      : '0px',
    bottom: isSmallScreen
      ? canShow && currentLokalplan
        ? '0vh'
        : '-40vh'
      : '0vh',
  });
  const baseStyle = {
    position: 'fixed' as PositionProperty,
    bottom: 0,
    left: 0,
    backgroundColor: common.backgroundColor,
    height: '40vh',
    zIndex: 4,
    boxShadow: common.boxShadow,
  };
  const style = isSmallScreen
    ? { ...baseStyle }
    : { ...baseStyle, left: 'unset', height: '100%', width: '430px' };

  if (clickedLokalplans.length === 0 || !currentLokalplan) {
    return <animated.div style={{ ...style, ...navAnimation }} />;
  }

  const renderClickedLokalplaner = clickedLokalplans.map((plan: ILokalplan) => {
    const selected =
      plan.feature.properties.planid ===
      currentLokalplan.feature.properties.planid;
    const details = (
      <LokalplanDetails
        selected={selected}
        lokalplan={plan}
        onListItemClick={props.onListItemClick}
        onListItemHover={props.onListItemHover}
        hoveredPlans={hoveredPlans}
      />
    );

    return (
      <div key={plan.feature.properties.planid}>
        <HeaderRow
          selected={
            plan.feature.properties.planid ===
            currentLokalplan.feature.properties.planid
          }
          onClick={() => {
            setCurrentLokaplan(plan);
          }}
        >
          <h2>{plan.feature.properties.plannr}</h2>
          <h1>{plan.feature.properties.plannavn}</h1>
          <h3>
            <span style={{ color: neutral[400] }}>PlanId:</span>{' '}
            {plan.feature.properties.planid}{' '}
            <span style={{ marginLeft: 40, color: neutral[400] }}>
              Version:
            </span>{' '}
            {plan.feature.properties.versionsnr}
          </h3>
        </HeaderRow>
        {details}
      </div>
    );
  });

  return (
    <animated.div style={{ ...style, ...navAnimation }}>
      <Times
        onClick={onClose}
        color={neutral[600]}
        width={18}
        height={18}
        style={{ position: 'absolute', top: 18, right: 18, cursor: 'pointer' }}
      />
      <ScrollWrapper>{renderClickedLokalplaner}</ScrollWrapper>
    </animated.div>
  );
};

const ScrollWrapper = styled.div`
  overflow-y: auto;
  height: 100%;
`;

const HeaderRow = styled.div`
  padding: 38px 32px 15px 32px;
  background-color: ${common.backgroundColor};
  border: 1px ${neutral[200]};
  border-style: none none solid none;
  box-shadow: 0 0 10px 0 rgba(59, 84, 111, 0.15);
  h1 {
    margin: 10px 0;
    font-size: 20px;
    line-height: 140%;
  }
  h3 {
    margin: 0 0 10px 0;
  }
  h2 {
    margin: 0;
    font-size: 15px;
  }
  ${props =>
    !props.selected &&
    css`
      cursor: pointer;
      border-style: solid none;
      > * {
        opacity: 0.7;
        transition: opacity 0.2s;
      }
      :hover > * {
        opacity: 1;
      }
    `}
`;

interface ILokalplanSidebarPropType {
  clickedLokalplans: Array<ILokalplan>;
  onClose?: Function;
  onListItemClick?: Function;
  onListItemHover?: Function;
  hoveredPlans: Array<number>;
  currentLokalplan?: ILokalplan;
  setCurrentLokaplan: Function;
  canShow: boolean;
}
export default LokalplanSidebar;
